import { useMemo } from 'react'
import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { ApiTokenManagementRow, ApiTokenType } from '../ApiTokenManagementRow'
import { formatDate, formatDateTime } from '../../../../utils/date'
import { intl } from '../../../../i18n'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { colorPalette } from '../../../style/colorPalette'
import { ReferencedEntityWithIcon } from '../../../../domain/value-object/ReferencedEntity'

const dateValueFormatter = (
  params: ValueFormatterParams<ApiTokenManagementRow, number>
): string => {
  if (!params.value) return ''
  return formatDate(params.value) ?? ''
}

const dateTimeValueFormatter = (
  params: ValueFormatterParams<ApiTokenManagementRow, number>
): string => {
  if (!params.value) return ''
  return formatDateTime(params.value) ?? ''
}

const scopesValueFormatter = (
  params: ValueFormatterParams<ApiTokenManagementRow, string[]>
): string => {
  if (!params.value || params.value.length === 0) return ''
  return params.value.join(', ')
}

const referencedEntityValueFormatter = (
  params: ValueFormatterParams<ApiTokenManagementRow, ReferencedEntityWithIcon>
): string => {
  if (!params.value) return ''
  return params.value.name
}

const getTokenTypeText = (tokenType: ApiTokenType | undefined): string => {
  if (!tokenType) return ''
  return intl.formatMessage({
    id:
      tokenType === 'service'
        ? 'apiTokenManagement.tokenType.service'
        : 'apiTokenManagement.tokenType.user',
  })
}

const getStatusText = (data: ApiTokenManagementRow | undefined): string => {
  if (!data) return ''
  if (data.revoked) {
    return intl.formatMessage({ id: 'apiTokenManagement.status.revoked' })
  }
  if (data.expired) {
    return intl.formatMessage({ id: 'apiTokenManagement.status.expired' })
  }
  return intl.formatMessage({ id: 'apiTokenManagement.status.active' })
}

const getStatusColor = (data: ApiTokenManagementRow | undefined): string => {
  if (!data) return ''
  if (data.revoked) return colorPalette.monotone[4]
  if (data.expired) return colorPalette.yellow[3]
  return colorPalette.green[5]
}

export const useColumnDefs = (): ColDef<ApiTokenManagementRow>[] => {
  const rowNumberColDef = useRowNumber()

  return useMemo(
    () => [
      {
        ...rowNumberColDef,
        pinned: 'left',
      },
      {
        field: 'tokenType',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.tokenType',
        }),
        width: 120,
        valueFormatter: params => getTokenTypeText(params.value),
      },
      {
        field: 'name',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.name',
        }),
        minWidth: 150,
      },
      {
        field: 'owner',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.owner',
        }),
        width: 150,
        minWidth: 100,
        valueFormatter: referencedEntityValueFormatter,
        cellStyle: params => {
          // サービストークンには所有者の概念がないためグレーアウト
          if (params.data?.tokenType === 'service') {
            return { backgroundColor: colorPalette.monotone[2] }
          }
          return undefined
        },
      },
      {
        field: 'createdBy',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.createdBy',
        }),
        width: 150,
        minWidth: 100,
        valueFormatter: referencedEntityValueFormatter,
      },
      {
        field: 'scopes',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.scopes',
        }),
        width: 250,
        minWidth: 150,
        valueFormatter: scopesValueFormatter,
      },
      {
        field: 'expiredAt',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.expiredAt',
        }),
        width: 120,
        valueFormatter: dateValueFormatter,
      },
      {
        field: 'createdAt',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.createdAt',
        }),
        width: 160,
        valueFormatter: dateTimeValueFormatter,
      },
      {
        field: 'lastUsedAt',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.lastUsedAt',
        }),
        width: 160,
        valueFormatter: dateTimeValueFormatter,
      },
      {
        colId: 'status',
        headerName: intl.formatMessage({
          id: 'apiTokenManagement.status',
        }),
        width: 120,
        valueGetter: params => getStatusText(params.data),
        cellStyle: params => ({
          color: getStatusColor(params.data),
          fontWeight: 'bold',
        }),
      },
    ],
    [rowNumberColDef]
  )
}
